import { Suspense, useMemo, useState } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Header } from '@/components/layout/Header';
import { ReportFiltersComponent } from '@/features/reports/components/ReportFilters';
import { ReportTable } from '@/features/reports/components/ReportTable';
import { ColumnSelector, DEFAULT_VISIBLE_COLUMNS } from '@/features/reports/components/ColumnSelector';
import { ExportButton } from '@/features/reports/components/ExportButton';
import { ChartViewToggle } from '@/features/reports/components/ChartViewToggle';
import { ReportCharts } from '@/features/reports/components/ReportCharts';
import { FadeIn } from '@/components/animations/FadeIn';
import { EmptyState } from '@/components/emptyState/EmptyState';
import { CardSkeleton } from '@/components/loading/LoadingState';
import type { ColumnKey } from '@/features/reports/components/ColumnSelector';
import type { ExportLocale, ReportFilters } from '@/features/reports/utils/exportToExcel';
import type { ReportsPageQuery as ReportsPageQueryType } from '@/__generated__/ReportsPageQuery.graphql.ts';
import { FileSpreadsheet } from 'lucide-react';

const reportsPageQuery = graphql`
  query ReportsPageQuery(
    $startDate: String
    $endDate: String
    $projectId: ID
    $billingStatus: BillingStatus
    $memberIds: [ID!]
  ) {
    tasks(
      startDate: $startDate
      endDate: $endDate
      projectId: $projectId
      billingStatus: $billingStatus
      memberIds: $memberIds
    ) {
      id
      date
      title
      description
      durationMinutes
      hourlyRate
      currency
      totalAmount
      billingStatus
      project {
        id
        name
        color
      }
      members {
        id
        name
      }
    }
    projects {
      id
      name
      color
    }
    users {
      id
      name
      email
    }
  }
`;

export function ReportsPage() {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('chart');
  const [locale, setLocale] = useState<ExportLocale>('en-US');

  const defaultDates = useMemo(() => {
    const now = new Date();
    return {
      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  }, []);

  const [filters, setFilters] = useState<ReportFilters>({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    projectId: null,
    billingStatus: null,
    memberIds: null,
  });

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex-1 space-y-6 p-4 sm:p-6">
        <FadeIn>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
            <p className="text-sm text-muted-foreground">
              Filter, review and export your tasks.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <CardTitle className="text-lg">Filters</CardTitle>
                <ChartViewToggle view={viewMode} onChange={setViewMode} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <Suspense fallback={<ReportsPageSkeleton />}>
                <ReportsPageContent
                  filters={filters}
                  onFiltersChange={setFilters}
                  visibleColumns={visibleColumns}
                  onVisibleColumnsChange={setVisibleColumns}
                  viewMode={viewMode}
                  locale={locale}
                  onLocaleChange={setLocale}
                />
              </Suspense>
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </div>
  );
}

function ReportsPageSkeleton() {
  return (
    <div className="space-y-6">
      <CardSkeleton />
      <CardSkeleton />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  );
}

function ReportsPageContent({
  filters,
  onFiltersChange,
  visibleColumns,
  onVisibleColumnsChange,
  viewMode,
  locale,
  onLocaleChange,
}: {
  filters: ReportFilters;
  onFiltersChange: (next: ReportFilters) => void;
  visibleColumns: ColumnKey[];
  onVisibleColumnsChange: (next: ColumnKey[]) => void;
  viewMode: 'table' | 'chart';
  locale: ExportLocale;
  onLocaleChange: (locale: ExportLocale) => void;
}) {
  const data = useLazyLoadQuery<ReportsPageQueryType>(reportsPageQuery, {
    startDate: filters.startDate,
    endDate: filters.endDate,
    projectId: filters.projectId,
    billingStatus: filters.billingStatus,
    memberIds: filters.memberIds,
  });

  return (
    <>
      <ReportFiltersComponent
        filters={filters}
        onChange={onFiltersChange}
        projects={data.projects}
        members={data.users}
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        <ColumnSelector value={visibleColumns} onChange={onVisibleColumnsChange} />
        <ExportButton
          entries={data.tasks}
          columns={visibleColumns}
          filters={filters}
          locale={locale}
          onLocaleChange={onLocaleChange}
        />
      </div>

      {data.tasks.length === 0 ? (
        <EmptyState
          title="No entries found"
          description="Try adjusting the filters or add a new task."
          icon={<FileSpreadsheet className="h-6 w-6" />}
        />
      ) : viewMode === 'table' ? (
        <div className="overflow-x-auto">
          <ReportTable entries={data.tasks} visibleColumns={visibleColumns} />
        </div>
      ) : (
        <ReportCharts entries={data.tasks} />
      )}
    </>
  );
}
